import React, { useMemo } from "react";
import { motion } from "framer-motion";
import BtnOLevel from "../../buttons/BtnOLevel";
import BtnOLevelResult from "../../buttons/BtnOLevelResult";
import BtnOLevelResultDel from "../../buttons/BtnOLevelResultDel";
import BtnDeleteOLevel from "../../buttons/BtnDeleteOLevel";
import { useQuery } from "@tanstack/react-query";
import { axiosget } from "../../helpers/httprequest";
import Loader from "../../components/Loader";
import { Alert, AlertDescription } from "../../components/ui/Alert";
import PageHeader from "../../components/PageHeader";
import { Badge } from "../../components/ui/Badge";
import EmptyState from "../../components/ui/EmptyState";
import { FiAward, FiBookOpen, FiMapPin, FiCalendar, FiPlus } from "react-icons/fi";

// Grade color variant helper
const getGradeVariant = (grade) => {
  const upperGrade = grade?.toUpperCase();
  if (["A1", "A", "B2", "B3"].includes(upperGrade)) return "success";
  if (["C4", "C5", "C6"].includes(upperGrade)) return "info";
  if (["D7", "E8"].includes(upperGrade)) return "warning";
  return "danger";
};

const OLevel = ({ wizardMode = false }) => {
  const { isLoading: sittingsLoading, error: sittingsError, data: sittings } = useQuery({
    queryKey: ["oLevelSitting"],
    queryFn: async () => {
      const req = await axiosget("ms_applicant_sitting");
      return req?.data;
    },
  });

  const { isLoading: resultsLoading, error: resultsError, data: results } = useQuery({
    queryKey: ["oLevelResult"],
    queryFn: async () => {
      const req = await axiosget("ms_applicant_result");
      return req?.data;
    },
  });

  const isLoading = sittingsLoading || resultsLoading;
  const error = sittingsError || resultsError;

  // Group results by sitting ID
  const resultsBySitting = useMemo(() => {
    if (!results || results?.type !== undefined) return {};
    const grouped = {};
    results.forEach((result) => {
      const sittingId = result?.examination?._id;
      if (sittingId) {
        if (!grouped[sittingId]) grouped[sittingId] = [];
        grouped[sittingId].push(result);
      }
    });
    return grouped;
  }, [results]);

  // Total counts
  const totalSubjects = useMemo(() => {
    if (!results || results?.type !== undefined) return 0;
    return results.length;
  }, [results]);

  const sittingsList = (!sittings || sittings?.type !== undefined) ? [] : sittings;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 },
    },
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: "easeOut" },
    },
  };

  return (
    <>
      {!wizardMode ? (
        <PageHeader
          title="O-Level Results"
          subtitle="Manage your examination sittings and results"
          icon={<FiAward className="w-5 h-5" />}
          action={!isLoading && sittingsList.length < 2 && <BtnOLevel />}
        />
      ) : (
        !isLoading && sittingsList.length < 2 && <div className="flex justify-end mb-3"><BtnOLevel /></div>
      )}

      {isLoading ? (
        <Loader />
      ) : error ? (
        <Alert variant="danger"><AlertDescription>Sorry, system could not find data</AlertDescription></Alert>
      ) : sittingsList.length === 0 ? (
        <EmptyState
          variant="no-data"
          title="No Examination Sittings Yet"
          description="You haven't added any examination sittings. Click the 'Add Sitting' button above to get started with your O-Level examination details."
          icon={<FiBookOpen className="h-12 w-12" />}
        />
      ) : (
        <>
          {/* Summary */}
          <div className="mb-6 px-4 py-3 bg-primary-50 rounded-lg border border-primary-200">
            <p className="text-sm text-primary-800 font-medium">
              {totalSubjects} subject{totalSubjects !== 1 ? "s" : ""} across {sittingsList.length} sitting{sittingsList.length !== 1 ? "s" : ""}
            </p>
          </div>

          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="space-y-6 mb-6"
          >
            {sittingsList.map((sitting, i) => {
              const sittingResults = resultsBySitting[sitting._id] || [];

              return (
                <motion.div key={sitting._id || i} variants={cardVariants}>
                  <div className="bg-white rounded-xl border border-neutral-200 shadow-sm overflow-hidden">
                    {/* Sitting Header */}
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-b border-green-100 px-5 py-4">
                      <div className="flex items-center justify-between flex-wrap gap-3">
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <Badge variant="success" size="sm">
                            {sitting.examinationtype}
                          </Badge>
                          <span className="text-sm font-semibold text-neutral-900">
                            {sitting.examinationnumber}
                          </span>
                          <div className="hidden sm:flex items-center gap-1 text-xs text-neutral-500">
                            <FiCalendar className="h-3 w-3" />
                            <span>{sitting.examinationyear}</span>
                          </div>
                          <div className="hidden sm:flex items-center gap-1 text-xs text-neutral-500">
                            <FiMapPin className="h-3 w-3" />
                            <span className="truncate max-w-[200px]">{sitting.examinationcenter}</span>
                          </div>
                        </div>

                        <div className="flex items-center gap-2">
                          <BtnOLevelResult defaultExamination={sitting._id} />
                          <BtnOLevel modules={sitting} />
                          <BtnDeleteOLevel modules={sitting} />
                        </div>
                      </div>
                    </div>

                    {/* Results Table */}
                    <div className="p-4">
                      {sittingResults.length === 0 ? (
                        <p className="text-sm text-neutral-400 text-center py-4">
                          No results added yet. Click "Add Result" to add subjects and grades.
                        </p>
                      ) : (
                        <div className="divide-y divide-neutral-100">
                          {sittingResults.map((result, j) => (
                            <div
                              key={result._id || j}
                              className="flex items-center justify-between py-2.5 px-2 hover:bg-neutral-50 rounded-lg transition-colors"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <span className="text-sm font-medium text-neutral-800">
                                  {result.subject}
                                </span>
                              </div>
                              <div className="flex items-center gap-3">
                                <Badge
                                  variant={getGradeVariant(result.scored)}
                                  size="sm"
                                  className="font-bold"
                                >
                                  {result.scored}
                                </Badge>
                                <div className="flex items-center gap-1">
                                  <BtnOLevelResult modules={result} />
                                  <BtnOLevelResultDel modules={result} />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </>
      )}
    </>
  );
};

export default OLevel;
